<script setup lang="ts">
import type { SbCalculatorStoryblok } from '~/types/storyblok'
import { roundToNearest, useMoneyFormat } from '@cfgtech/helpers'
import { QuestionCircleIcon } from '@cfgtech/icons'
import { CfgButton, CfgHint, CfgInput, CfgSlider, CfgTypography, CfgTypographySizes } from '@cfgtech/ui'

defineProps<{ blok: SbCalculatorStoryblok }>()

const DEFAULT_MAX = 1000000
const DEFAULT_START = 100000
const DEFAULT_STEP = 5000
const DEFAULT_MIN = 5000

const { public: { dmatWebUrl } } = useRuntimeConfig()
const { n, t } = useI18n()
const { generalInvestmentEstimate, fetchIssuesGeneralInvest } = useIssues()

const amount = ref(DEFAULT_START)
const amountInputValue = ref(useMoneyFormat(amount.value, true)) // amountInputValue is same as amount but formatted

const min = ref(DEFAULT_MIN)
const max = ref(DEFAULT_MAX)
const step = ref(DEFAULT_STEP)

type CalculatorData = {
  title: string
  value: string
  tooltip: string
  valueClass?: string
}

const data = computed<CalculatorData[]>(() => [
  {
    title: t('SBCalculator.result.monthlyWithoutTax.title'),
    value: n(getSafeNumber(generalInvestmentEstimate.value?.monthlyPayout), 'currencyWithoutDecimals'),
    tooltip: t('SBCalculator.result.monthlyWithoutTax.tooltip'),
  },
  {
    title: t('SBCalculator.result.totalWithoutTax.title'),
    value: n(getSafeNumber(generalInvestmentEstimate.value?.net), 'currencyWithoutDecimals'),
    tooltip: t('SBCalculator.result.totalWithoutTax.tooltip'),
    valueClass: 'text-brand-dark',
  },
])

const investRate = computed(() => {
  return n(getSafeNumber(generalInvestmentEstimate.value?.interestRate) / 100, 'percentTwoDecimals')
})

const durations = ref([
  { label: t('SBCalculator.duration.select.1-to-2'), value: 2 },
  { label: t('SBCalculator.duration.select.3'), value: 3 },
  { label: t('SBCalculator.duration.select.5'), value: 5 },
])
const selectedDuration = ref(3)

function autocompleteInput(): void {
  const value = (amountInputValue.value.toString()).replace(/\s/g, '')

  const roundedResult = roundToNearest(Number(value), {
    min: min.value,
    max: max.value,
    step: step.value,
  })

  amount.value = roundedResult
  amountInputValue.value = useMoneyFormat(roundedResult, true)
}

watchDebounced(
  [amount, selectedDuration],
  async () => {
    await fetchIssuesGeneralInvest(amount.value, selectedDuration.value)
  },
  { debounce: 300, maxWait: 1000, immediate: true },
)

watch(amount, () => {
  const formattedInvestment = useMoneyFormat(amount.value, true)

  if (amountInputValue.value !== formattedInvestment) {
    amountInputValue.value = useMoneyFormat(amount.value, true)
  }
})
</script>

<template>
  <div v-editable="blok" class="grid-cols-12 gap-x-8 rounded-3xl bg-white shadow-card max-lg:-mx-4 lg:grid lg:p-3">
    <div class="col-span-6 flex flex-col p-5 max-lg:pb-10 lg:p-8">
      <CfgTypography class="max-w-[370px] !font-extrabold" :size="CfgTypographySizes.h2">
        {{ $t('SBCalculator.title') }}
      </CfgTypography>

      <div class="mt-7 flex flex-col">
        <div class="flex size-full items-center justify-between pb-3">
          <CfgTypography class="grow !font-semibold" :size="CfgTypographySizes.md">
            {{ $t('calculator.amount') }}
          </CfgTypography>

          <div>
            <CfgInput
              v-model="amountInputValue"
              class="!w-[150px] !font-semibold"
              field-class="cfg-input__super-small [&>span]:!py-0 [&>span]:flex [&>span]:items-center [&>span]:font-semibold"
              style="--input-height: 3.6px"
              :suffix="$t('common.currencies.CZK')"
              type="text"
              @blur="autocompleteInput"
              @keydown.enter="autocompleteInput"
            />
          </div>
        </div>

        <CfgSlider
          v-model="amount"
          class="mt-3 self-stretch [&_.cfg-slider]:border-0 [&_.cfg-slider]:p-0"
          connect="lower"
          :lazy="false"
          :max
          :min
          :step
          :tooltips="false"
        />
      </div>

      <div class="mt-8 flex w-full flex-col lg:mt-12">
        <div class="inline-flex w-full items-center gap-x-3">
          <span>
            {{ $t('SBCalculator.duration.title') }}
          </span>

          <CfgHint hide-icon>
            <template #trigger>
              <QuestionCircleIcon class="text-[18px]" />
            </template>

            <template #content>
              <CfgTypography
                :html="$t('SBCalculator.duration.tooltip')"
                :size="CfgTypographySizes.sm"
              />
            </template>
          </CfgHint>
        </div>

        <div class="mt-5 inline-flex w-full overflow-hidden rounded-full border border-orange text-center !font-normal text-orange">
          <button
            v-for="duration in durations"
            :key="duration.value"
            class="w-1/3 py-[6px] transition duration-300"
            :class="{
              'border-r border-orange': duration.value !== durations[durations.length - 1].value,
              'bg-orange-light': selectedDuration === duration.value,
            }"
            @click="selectedDuration = duration.value"
          >
            <CfgTypography
              :class="{
                '!font-semibold': selectedDuration === duration.value,
              }"
              :size="CfgTypographySizes.md"
            >
              {{ duration.label }}
            </CfgTypography>
          </button>
        </div>
      </div>
    </div>

    <div class="relative col-span-6 rounded-2xl bg-brand-dark px-4 py-8 lg:p-8">
      <CfgTypography class="!font-extrabold text-white" :size="CfgTypographySizes.h3">
        {{ $t('SBCalculator.result.title') }}
      </CfgTypography>

      <div class="mt-7 grid grid-cols-2 flex-wrap rounded-2xl bg-white px-6 py-5 lg:gap-5">
        <div
          v-for="item in data"
          :key="item.title"
          class="flex flex-col gap-1 lg:gap-2"
        >
          <CfgHint>
            <template #trigger>
              <CfgTypography class="lg:!heading-sm heading-xs !font-semibold text-secondary">
                {{ item.title }}
              </CfgTypography>
            </template>

            <template #content>
              <CfgTypography :size="CfgTypographySizes.sm">
                {{ item.tooltip }}
              </CfgTypography>
            </template>
          </CfgHint>

          <CfgTypography
            class="!font-extrabold"
            :class="item.valueClass"
            :size="CfgTypographySizes.h2"
          >
            {{ item.value }}
          </CfgTypography>
        </div>
      </div>

      <CfgTypography
        class="mt-5 block text-white"
        :html="$t('SBCalculator.result.description', { rate: investRate })"
        :size="CfgTypographySizes.sm"
      />

      <CfgButton
        class="z-[1] mt-10 w-full max-w-[250px] lg:max-w-[300px]"
        variant="orange"
        @click="navigateTo(`${dmatWebUrl}/auth/register/email`, { external: true })"
      >
        {{ $t('SBCalculator.button') }}
      </CfgButton>

      <img
        v-if="blok.picture.filename"
        :alt="blok.picture.alt || ''"
        class="absolute bottom-0 right-0 z-0 h-[120px] w-[139px]"
        height="120px"
        loading="lazy"
        :src="blok.picture.filename || ''"
        width="139px"
      >
    </div>
  </div>
</template>
